/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Box, useTheme, useMediaQuery, Button, Typography, Modal, FormHelperText, IconButton } from "@mui/material";
import "@styles/stylesRegistro.css";
import { PhoneCodeInput } from "@components/PhoneCodeInput";
import { CustomTextCiecBuro } from "@components/CustomTextCiecBuro";
import { PERSON_CATALOG, STATUS_CATALOG } from "@helpers/constantes";
import { BuroValidatorRequestPFAE, useBuroValidatorMutation } from "@api/sat_apis_new/satApsi";
import { usePostValidateNextStageMutation } from "@api/applications";
import { setModalErrorVisible, setProcessPage } from "@store/slices/appSlice";
import { RootState, useAppDispatch } from "@store/store";
import { useSelector } from "react-redux";
import { ApiError } from "@interfaces/response";
import * as Sentry from "@sentry/react";
import { setCheckSatBureau } from "@store/slices/registerSlice";
import { Close as CloseIcon } from "@mui/icons-material";
import useStatusConfig from "@hooks/useStatusConfig";
import { truncateText } from "@helpers/funciones";
import { useLazyGetPersonTypeQuery } from "@api/catalogs";
import {
  useLazyGetPersonByIdQuery,
  useLazyGetPersonsQuery,
  usePatchPersonMutation,
  usePostPersonMutation,
} from "@api/personasApi";
import { PayloadPersonPatch } from "@interfaces/request";

interface ModalBuroNipProps {
  screenStatus: string;
  open: boolean;
  onClose: () => void;
}

const ModalBuroNip = (props: ModalBuroNipProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus } = useStatusConfig(props.screenStatus);

  const buroNip = useSelector((state: RootState) => state.register.buroNip);
  const applicationData = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const companyData = useSelector((state: RootState) => state.register.company);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);

  const [confirmCode, setConfirmCode] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [personTypeId, setPersonTypeId] = useState<string[]>([]);
  const [personId, setPersonId] = useState<string>("");
  const [isCreditBureauAuthorizationPfSended, setIsCreditBureauAuthorizationPfSended] = useState<boolean | null>(null);
  const [isCreditBureauAuthorizationPfCorrect, setIsCreditBureauAuthorizationPfCorrect] = useState<boolean | null>(
    null,
  );

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [buroValidator] = useBuroValidatorMutation();
  const [getPersonTypeData] = useLazyGetPersonTypeQuery();
  const [getPersonById] = useLazyGetPersonByIdQuery();
  const [getPersonsByLoandId] = useLazyGetPersonsQuery();
  const [saveLegalRepresentative] = usePostPersonMutation();
  const [patchPerson] = usePatchPersonMutation();

  const continueToWaitingRoom = async () => {
    try {
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }

      await triggerPostNextStage({
        applicationId: applicationData.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setProcessPage(STATUS_CATALOG.waiting_room.url));
      dispatch(setCheckSatBureau({ check_sat_bureau: true }));
      navigate(nextStatus.url);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "ConsultaBuro",
          values: {},
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  const handleClickConfirm = async () => {
    try {
      setIsLoading(true);

      // se limita a 26 caracteres por campo, ya que es el máximo permitido por BURO
      const legal_name = truncateText(companyData.name || "", 26);
      const first_name = truncateText(companyData.pfae_name || "", 26);
      const first_lastname = truncateText(companyData.pfae_last_name || "", 26);
      const second_lastname = truncateText(companyData.pfae_last_name_2 || "", 26);

      const payload_buro: BuroValidatorRequestPFAE = {
        person_Type: "pf",
        data: {
          rfc: companyData.rfc,
          transaction_number: applicationData.id as string,
          legal_name: legal_name,
          first_name: first_name,
          second_name: "",
          father_last_name: first_lastname,
          mother_last_name: second_lastname,
          reenter_NIP: true,
          authorization_label_response: true,
          loan_application_id: applicationData.id as string,
          is_endorsement: false,
          person_id: personId,
        },
      };

      const payLegalRepresentativeData: PayloadPersonPatch = {
        person_types: personTypeId,
        name: clientDetails.name,
        last_name: clientDetails.first_lastname,
        last_name_2: clientDetails.second_lastname,
        loan_application_id: applicationData.id,
        code_phone_number: clientDetails.country_code,
        phone_number: clientDetails.phone,
        email: accountUser.email,
        rfc: companyData.rfc,
        is_shareholder: true,
      };

      if (personId) {
        await patchPerson({
          personId: personId,
          body: payLegalRepresentativeData,
        }).unwrap();
      } else {
        await saveLegalRepresentative({ body: payLegalRepresentativeData }).unwrap();
      }
      if (!isCreditBureauAuthorizationPfSended || !isCreditBureauAuthorizationPfCorrect) {
        await buroValidator(payload_buro).unwrap();
      }
      
      await continueToWaitingRoom();
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.status === 409) {
        await continueToWaitingRoom();
      } else {
        Sentry.captureException(error, {
          extra: {
            component: "ConsultaBuro",
            values: {},
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: apiError }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const personTypeResponse = await getPersonTypeData().unwrap();

        const accionistaMayoritario = personTypeResponse.find(
          (item) => item.code === PERSON_CATALOG.accionista_mayoritario.code,
        );
        const representanteLegal = personTypeResponse.find(
          (item) => item.code === PERSON_CATALOG.representante_legal.code,
        );
        const personTypeId: string[] = [];

        if (accionistaMayoritario?.id) {
          personTypeId.push(String(accionistaMayoritario.id));
        }

        if (representanteLegal?.id) {
          personTypeId.push(String(representanteLegal.id));
        }

        setPersonTypeId(personTypeId);

        const responseGetPersons = await getPersonsByLoandId({
          loanApplicationId: applicationData.id,
        }).unwrap();

        if (responseGetPersons.length > 0) {
          const responseGetPerson = await getPersonById(responseGetPersons[0].id).unwrap();
          setPersonId(String(responseGetPerson.id));
          setIsCreditBureauAuthorizationPfSended(responseGetPerson.is_credit_bureau_authorization_pf_sended);
          setIsCreditBureauAuthorizationPfCorrect(responseGetPerson.is_credit_bureau_authorization_pf_correct);
        }
      } catch (error) {
        const api_error = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Modal open={props.open}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          textAlign: "center",
          width: "70%",
          maxWidth: "400px",
          outline: "none",
        }}
      >
        <IconButton
          aria-label="close"
          onClick={props.onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography fontWeight={"600"} gutterBottom color={theme?.palette?.primary?.dark}>
          Consulta a Buró de crédito
        </Typography>

        <Typography
          variant={isMobileOrTablet ? "body2" : "body1"}
          sx={{ mt: 2, mb: 2 }}
          color={theme?.palette?.primary?.dark}
        >
          <CustomTextCiecBuro type="nipBuroConfirm" />
        </Typography>

        <Typography
          variant={isMobileOrTablet ? "body2" : "body1"}
          sx={{ mt: 2, mb: 2 }}
          color={theme?.palette?.primary?.dark}
        >
          Confirma tu Código
        </Typography>

        <PhoneCodeInput
          name="code_confirm"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setConfirmCode(event.target.value);
          }}
        />

        {confirmCode.length === 4 && confirmCode !== buroNip.code && (
          <FormHelperText sx={{ mt: 1 }}>
            El código ingresado no es correcto. Por favor verifica el código y vuelve a intentarlo.
          </FormHelperText>
        )}

        <Button
          variant="default"
          color="primary"
          sx={{ mt: 2 }}
          onClick={handleClickConfirm}
          disabled={confirmCode !== buroNip.code || isLoading}
        >
          Confirmar
        </Button>
      </Box>
    </Modal>
  );
};

export default ModalBuroNip;
